import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http'
import { NgModule, provideZoneChangeDetection } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { RouteReuseStrategy } from '@angular/router'
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular'
import { SharedModule } from '@shared/shared.module'
import { provideCharts, withDefaultRegisterables } from 'ng2-charts'

import { environment } from '../environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { ShowcaseIsolationInterceptor } from './services/showcase-isolation.interceptor'
import { DevWakeupInterceptor } from './services/dev-wakeup.interceptor'
import { ListPageReuseStrategy } from './services/list-page-reuse-strategy'
import { ZoneAwareInterceptor } from './services/zone-aware.interceptor'
import { OrganizationSignInInterceptor } from './services/organization-sign-in.interceptor'

const allowedList = [
    { uri: `${environment.api}/admin*` },
    { uri: `${environment.api}/analytics*` },
    { uri: `${environment.api}/api-keys*` },
    { uri: `${environment.api}/asset*` },
    { uri: `${environment.api}/auth*` },
    { uri: `${environment.api}/action*` },
    { uri: `${environment.api}/builder*` },
    { uri: `${environment.api}/connection*` },
    { uri: `${environment.api}/copy*` },
    { uri: `${environment.api}/feature*` },
    { uri: `${environment.api}/feedback` },
    { uri: `${environment.api}/files*` },
    { uri: `${environment.api}/interaction*` },
    { uri: `${environment.api}/job*` },
    { uri: `${environment.api}/me*` },
    { uri: `${environment.api}/model*` },
    { uri: `${environment.api}/oee*` },
    { uri: `${environment.api}/ops*` },
    { uri: `${environment.api}/org*` },
    { uri: `${environment.api}/organization*` },
    { uri: `${environment.api}/permission*` },
    { uri: `${environment.api}/project*` },
    { uri: `${environment.api}/public/connections/veckta` },
    { uri: `${environment.api}/reset-password*` },
    { uri: `${environment.api}/s3*` },
    { uri: `${environment.api}/scene*` },
    { uri: `${environment.api}/search*` },
    { uri: `${environment.api}/sign-up*` },
    { uri: `${environment.api}/stripe*` },
    { uri: `${environment.api}/subscription*` },
    { uri: `${environment.api}/user*` },
    { uri: `${environment.api}/viewer/project*` },
]

type AuthConfigWithLocalDefaults = typeof environment.auth & {
    authorizationParams: typeof environment.auth.authorizationParams & { scope?: string }
    useCookiesForTransactions?: boolean
    useRefreshTokens?: boolean
    useRefreshTokensFallback?: boolean
}

const environmentAuth = environment.auth as AuthConfigWithLocalDefaults
const authConfig = {
    ...environmentAuth,
    ...(!environment.production ? {
        useRefreshTokens: environmentAuth.useRefreshTokens ?? true,
        useRefreshTokensFallback: environmentAuth.useRefreshTokensFallback ?? false,
        useCookiesForTransactions: environmentAuth.useCookiesForTransactions ?? true,
    } : {}),
    authorizationParams: {
        ...environmentAuth.authorizationParams,
        ...(!environment.production ? {
            scope: environmentAuth.authorizationParams.scope ?? 'openid profile email offline_access',
        } : {}),
    },
}

@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        AppRoutingModule,
        AuthModule.forRoot({
            ...authConfig,
            httpInterceptor: {
                allowedList,
            },
        }),
        BrowserAnimationsModule,
        FormsModule,
        HttpClientModule,
        MatSnackBarModule,
        ReactiveFormsModule,
        SharedModule,
    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: ShowcaseIsolationInterceptor, multi: true },
        // Explicitly enable zone-based change detection (required in Angular 21 with application builder)
        provideZoneChangeDetection({ eventCoalescing: true }),
        // ng2-charts provider for Chart.js registration (required for ng2-charts 6+)
        provideCharts(withDefaultRegisterables()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ZoneAwareInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthHttpInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DevWakeupInterceptor,
            multi: true,
        },
        { provide: HTTP_INTERCEPTORS, useClass: OrganizationSignInInterceptor, multi: true },
        {
            provide: Window,
            useValue: window,
        },
        {
            provide: RouteReuseStrategy,
            useClass: ListPageReuseStrategy,
        },
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
